import axios from "axios";
import { useCallback } from "react";
import { useQueryClient } from "@tanstack/react-query";
import useAPI, {
  handleAuthRetry,
  isHandledLinkedAccountRefusal,
  requestLogin,
  useAPIHelper as fetchAPIData,
  useAPIMutation
} from "@/services/api/hook";
import { ExtendedUseQueryResult } from "@/services/api/types";
import { useAuth } from "@/services/auth/AuthContext";
import { webFormErrorDetail } from "@/entities/webForm/hooks";
import {
  ContractSubmissionSettings,
  FulfillmentUpdate,
  GeneralUpdate,
  StripeConnectionSettings,
  StripeConnectionUpdate,
  StripeMappingUpdate,
  StripeSecretsUpdate,
  StripeSourceUpdate,
  StripeTestResult
} from "@/entities/stripeCheckout/types";

const STRIPE_SETTINGS_KEY = "stripe/settings";
const SUBMISSION_SETTINGS_KEY = "contractSubmissions/settings";

// Every route needs the Settings admin permission, so callers pass `enabled`
// off for anyone else rather than letting the request fail with a 403.
export const useStripeConnectionSettings = (
  enabled = true
): ExtendedUseQueryResult<StripeConnectionSettings> =>
  useAPI(STRIPE_SETTINGS_KEY, undefined, { enabled });

export const useContractSubmissionSettings = (
  enabled = true
): ExtendedUseQueryResult<ContractSubmissionSettings> =>
  useAPI(SUBMISSION_SETTINGS_KEY, undefined, { enabled });

// Returning the refetch keeps a save pending until the saved state shows.
const useRefetch = (queryKey: string) => {
  const queryClient = useQueryClient();
  return useCallback(
    () => queryClient.invalidateQueries({ queryKey: [queryKey] }),
    [queryClient, queryKey]
  );
};

export const useSaveStripeConnection = () =>
  useAPIMutation<{ data: StripeConnectionUpdate }, StripeConnectionSettings>(
    "stripe/settings/connection",
    { method: "PUT" },
    useRefetch(STRIPE_SETTINGS_KEY)
  );

// Checks the saved key against Stripe; nothing is saved, so nothing refetches.
export const useTestStripeConnection = () =>
  useAPIMutation<Record<string, never>, StripeTestResult>(
    "stripe/settings/test",
    { method: "POST" }
  );

export const useSaveSubmissionGeneral = () =>
  useAPIMutation<{ data: GeneralUpdate }, ContractSubmissionSettings>(
    "contractSubmissions/settings/general",
    { method: "PUT" },
    useRefetch(SUBMISSION_SETTINGS_KEY)
  );

export const useSaveSubmissionFulfillment = () =>
  useAPIMutation<{ data: FulfillmentUpdate }, ContractSubmissionSettings>(
    "contractSubmissions/settings/fulfillment",
    { method: "PUT" },
    useRefetch(SUBMISSION_SETTINGS_KEY)
  );

export const useSaveStripeSource = () =>
  useAPIMutation<{ data: StripeSourceUpdate }, ContractSubmissionSettings>(
    "contractSubmissions/settings/stripe",
    { method: "PUT" },
    useRefetch(SUBMISSION_SETTINGS_KEY)
  );

export const useSaveStripeMapping = () =>
  useAPIMutation<{ data: StripeMappingUpdate }, ContractSubmissionSettings>(
    "contractSubmissions/settings/stripe/mapping",
    { method: "PUT" },
    useRefetch(SUBMISSION_SETTINGS_KEY)
  );

export type StripeSecretsResult =
  | { status: "saved" }
  // `message` is the backend's validation message, which never echoes a secret.
  | { status: "rejected"; message?: string }
  | { status: "failed" };

// Sent outside react-query on purpose: a mutation keeps its variables in the
// mutation cache, and a secret must not outlive the request in the browser.
export const useSaveStripeSecrets = () => {
  const { token, tenantId, tokenExpiry, login } = useAuth();
  const refetch = useRefetch(STRIPE_SETTINGS_KEY);

  return useCallback(
    async (secrets: StripeSecretsUpdate): Promise<StripeSecretsResult> => {
      try {
        await fetchAPIData({ token, tenantId, tokenExpiry }, [
          "stripe/settings/secrets",
          { method: "PUT", data: secrets }
        ]);
      } catch (error) {
        // Reduce the failure to a status here: the request error holds the secrets in its config
        if (handleAuthRetry(0, error)) {
          if (!isHandledLinkedAccountRefusal(error)) {
            requestLogin(login);
          }
          return { status: "failed" };
        }
        if (axios.isAxiosError(error) && error.response?.status === 422) {
          return { status: "rejected", message: webFormErrorDetail(error) };
        }
        return { status: "failed" };
      }
      await refetch().catch(() => {});
      return { status: "saved" };
    },
    [token, tenantId, tokenExpiry, login, refetch]
  );
};
